import { Button } from '@creditornot/cb-button';
import { AlchemyStar, CaretLeft } from '@creditornot/cb-icons';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { color } from '@creditornot/cb-ingredients/design-tokens';
import { Spinner } from '@creditornot/cb-progress';
import { useQuery } from '@tanstack/react-query';
import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { getImageEnhancementTemplates } from 'apis/asset-repository-service/api';
import { Asset, PromotedAsset } from 'apis/asset-repository-service/types';
import { ConfirmModal } from 'common/components';
import { Typography as T } from 'common/typography';

import { ImageCardWithEnhancement } from './ImageCardWithEnhancement';
import { ImageGalleryHeader } from './ImageGalleryHeader';
import { TemplateCard } from './TemplateCard';
import { IMAGE_BASE_URL } from '../constants';
import { useListingManagerImageGalleryEnhancementsStream } from '../hooks/useListingManagerImageGalleryEnhancementsStream';
import { useListingManagerImageGalleryFeatureFlag } from '../hooks/useListingManagerImageGalleryFeatureFlag';

export type ImageToEnhance = {
  id: Asset['id'];
  name?: string;
};

type Props = {
  imagesToEnhance: ImageToEnhance[];
  loading?: boolean;
  loadingCaption?: ReactNode;
  onDeleteImage: (imageId: Asset['id']) => void;
  onSaveResult: (enhancedImages: PromotedAsset[]) => void;
  onClose: () => void;
};

const Root = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: ${color.bg};
`;

const Loading = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  padding-block-start: 40px;
  padding-block-end: 40px;
`;

const Content = styled.div`
  display: flex;
  overflow: hidden;
`;

const EditPanel = styled.div`
  width: 290px;
  flex-shrink: 0;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 28px;
  height: 100%;
  overflow: auto;
`;

const EditPanelContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const EditPanelTitle = styled(T.Title1)`
  font-weight: 700;
`;

const PreviewPanel = styled.div`
  overflow: auto;
  flex: 1;
  border-inline-start: 1px solid ${cbSemanticCssVars.borderPrimary};
  background-color: ${color.bgSurfaceBrandDisabled};
  padding: 20px;
`;

const ImagesPanel = styled.div`
  width: 140px;
  flex-shrink: 0;
  padding: 20px;
  border-inline-start: 1px solid ${cbSemanticCssVars.borderPrimary};
  display: flex;
  flex-direction: column;
  gap: 12px;
  height: 100%;
  overflow: auto;
`;

export const ImageGalleryEnhanceImages = ({
  imagesToEnhance,
  loading,
  loadingCaption,
  onDeleteImage,
  onSaveResult,
  onClose,
}: Props) => {
  const intl = useIntl();
  const { isLoading: isFeatureFlagLoading } = useListingManagerImageGalleryFeatureFlag();
  const { data: templates, isLoading: isTemplatesLoading } = useQuery({
    queryKey: ['listing-manager-image-gallery-templates'],
    queryFn: getImageEnhancementTemplates,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [previewImageId, setPreviewImageId] = useState<string | null>(null);
  const [isDiscardChangesModalOpen, setIsDiscardChangesModalOpen] = useState<boolean>(false);
  const { imageEnhancements, promoteEnhancements, isLoadingEnhancements, isPromotingEnhancements } =
    useListingManagerImageGalleryEnhancementsStream({
      assetIds: imagesToEnhance.map(image => image.id),
      templateId: selectedTemplateId,
    });

  useEffect(() => {
    if (imagesToEnhance.length > 0) {
      setPreviewImageId(imagesToEnhance[0].id);
    } else {
      setPreviewImageId(null);
    }
  }, [imagesToEnhance]);

  const isLoadingContent = useMemo(
    () => isFeatureFlagLoading || isTemplatesLoading,
    [isFeatureFlagLoading, isTemplatesLoading],
  );

  const handleDeleteImage = (id: string) => {
    const image = imagesToEnhance.find(image => image.id === id);
    if (!image) {
      return;
    }

    onDeleteImage(image.id);
  };

  const handleClose = () => {
    if (selectedTemplateId && imagesToEnhance.length > 0) {
      setIsDiscardChangesModalOpen(true);
      return;
    }

    onClose();
  };

  const handleSave = () => {
    promoteEnhancements(onSaveResult);
  };

  return (
    <Root>
      {isLoadingContent || loading ? (
        <Loading>
          <Spinner size="large" />
          {loading && loadingCaption}
        </Loading>
      ) : (
        <>
          <ImageGalleryHeader
            title={intl.formatMessage(
              { id: 'shared-menu.editor.image-gallery.edit-images.header-title' },
              { count: imagesToEnhance.length },
            )}
            onClose={handleClose}
            hasCloseButton
            closeIcon={<CaretLeft />}
          >
            <Button
              variant="primary"
              size="tiny"
              disabled={
                !selectedTemplateId || imagesToEnhance.length < 1 || isLoadingEnhancements || isPromotingEnhancements
              }
              icon={<AlchemyStar />}
              onClick={handleSave}
            >
              {intl.formatMessage({ id: 'shared-menu.editor.image-gallery.edit-images.save-as-new-images' })}
            </Button>
          </ImageGalleryHeader>
          <Content>
            <EditPanel>
              <EditPanelContentContainer>
                <EditPanelTitle>
                  <FormattedMessage id="shared-menu.editor.image-gallery.edit-images.title" />
                </EditPanelTitle>
                <T.Body3>
                  <FormattedMessage id="shared-menu.editor.image-gallery.edit-images.subtitle" />
                </T.Body3>
              </EditPanelContentContainer>
              <EditPanelContentContainer>
                <TemplateCard
                  key={'empty-template'}
                  template={{
                    id: 'empty-template',
                    name: intl.formatMessage({ id: 'shared-menu.editor.image-gallery.edit-images.no-template' }),
                    background: '',
                  }}
                  selected={selectedTemplateId === null}
                  onClick={() => setSelectedTemplateId(null)}
                  disabled={isPromotingEnhancements}
                />
                {templates?.map(template => (
                  <TemplateCard
                    key={template.id}
                    template={template}
                    selected={template.id === selectedTemplateId && !isPromotingEnhancements}
                    onClick={setSelectedTemplateId}
                    disabled={isLoadingEnhancements || isPromotingEnhancements}
                  />
                ))}
              </EditPanelContentContainer>
            </EditPanel>
            <PreviewPanel>
              {previewImageId && (
                <ImageCardWithEnhancement
                  id={previewImageId}
                  src={IMAGE_BASE_URL + previewImageId}
                  name={imagesToEnhance.find(image => image.id === previewImageId)?.name}
                  imageEnhancement={imageEnhancements[previewImageId]}
                  loading={isLoadingEnhancements && !imageEnhancements[previewImageId]}
                />
              )}
            </PreviewPanel>
            <ImagesPanel>
              {imagesToEnhance.map(image => (
                <ImageCardWithEnhancement
                  id={image.id}
                  key={image.id}
                  src={IMAGE_BASE_URL + image.id}
                  imageEnhancement={imageEnhancements[image.id]}
                  loading={(isLoadingEnhancements && !imageEnhancements[image.id]) || isPromotingEnhancements}
                  onClick={() => setPreviewImageId(image.id)}
                  active={previewImageId === image.id}
                  onDelete={handleDeleteImage}
                  deleteDisabled={isPromotingEnhancements}
                  thumbnail
                />
              ))}
            </ImagesPanel>
          </Content>
          <ConfirmModal
            open={isDiscardChangesModalOpen}
            header={<FormattedMessage id="dropdown.areYouSure" />}
            description={<FormattedMessage id="shared-menu.editor.image-gallery.confirm-discard-changes-info" />}
            cancelText={<FormattedMessage id="state.cancel" />}
            confirmText={<FormattedMessage id="state.confirm" />}
            onCancel={() => setIsDiscardChangesModalOpen(false)}
            onConfirm={onClose}
          />
        </>
      )}
      ;
    </Root>
  );
};
